import { Link, type LinkProps } from "@tanstack/react-router";
import { useState, type ComponentType, type ReactNode } from "react";

import type { AnimatedIconName } from "./animated-icons.js";
import { NavIcon } from "./nav-icon.js";

import {
  NAV_ICON,
  NAV_ICON_LIFT,
  NAV_LABEL,
  NAV_PILL_ACTIVE,
  NAV_PILL_BASE,
  NAV_PILL_IDLE,
} from "./nav-pill.js";

/**
 * One destination in the rail.
 *
 * THE LABEL IS RENDERED AND `aria-label` STAYS. It was strictly necessary while the label was hidden
 * on a phone bar of icons — a hidden element contributes nothing to the accessible name, so without
 * the attribute every link announced as "link". The bar is gone and the label is visible everywhere
 * now, which makes the attribute redundant in the literal sense and worth keeping anyway: it pins the
 * accessible name to one string that no future layout change can alter, and 47 e2e locators reach
 * these links by exactly that name.
 */
export function NavLink({
  to,
  label,
  icon,
  animation,
  onNavigate,
}: {
  // `NonNullable` because `exactOptionalPropertyTypes` is on: `LinkProps["to"]` admits `undefined`
  // (a `<Link>` with no destination is a valid same-page link), and a rail item without one is not.
  to: NonNullable<LinkProps["to"]>;
  label: string;
  icon: ComponentType<{ className?: string }>;
  animation: AnimatedIconName;
  /**
   * Called when this link is followed — for the mobile drawer, which has to shut behind it.
   *
   * A NAVIGATION DOES NOT UNMOUNT THE SHELL. TanStack Router swaps the `<Outlet />` and leaves the
   * layout in place, so a drawer left open sits over the page it just navigated to — and being a
   * modal `<dialog>`, it makes that page inert. The symptom is an application that stops responding
   * after one tap, which reads as a hang rather than as an open menu.
   */
  onNavigate?: () => void;
}): ReactNode {
  /*
   * THE STATE IS HERE, ON THE PILL, AND NOT INSIDE THE ICON.
   *
   * The pill is 48px on a phone and 64px on the rail; the icon inside it is 34px. An icon that listened
   * for its own pointer events would ignore the ring of pill around it — so pointing at the edge of a
   * target would highlight it and not animate it, which reads as the animation being broken rather than
   * as having missed by 8px.
   *
   * `pointerenter`/`pointerleave` rather than `mouseover`/`mouseout`: those two fire again for every
   * child element crossed, so the animation would restart as the pointer moved between the SVG's paths.
   */
  const [hot, setHot] = useState(false);

  return (
    <Link
      to={to}
      aria-label={label}
      title={label}
      onPointerEnter={() => setHot(true)}
      onPointerLeave={() => setHot(false)}
      /*
       * Focus and blur as well, so the rail animates for a keyboard and not only for a pointer.
       *
       * THERE IS NO CLICK HANDLER, and none is needed. `pointerenter` fires on a TAP as well as on a
       * hover, so a touch device animates on the tap that navigates; a mouse click is always preceded by
       * the hover that already played it; and a keyboard activation follows the focus that did. A click
       * handler on top would restart an animation already running — a visible jitter at the exact moment
       * the page is changing.
       */
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
      /*
       * The one click handler this component has, and it is not about the animation — the note above
       * explains why the animation deliberately has none. It exists for the drawer.
       */
      {...(onNavigate === undefined ? {} : { onClick: onNavigate })}
      className={NAV_PILL_BASE}
      /*
       * Every route in the app is a child of "/", so the default prefix match would leave Dashboard
       * lit on every page. Exactness is right for it and wrong for the others, which will grow
       * detail routes (`/properties/$id`) that should keep their section highlighted.
       */
      activeOptions={{ exact: to === "/" }}
      // Only the state classes here: `Link` CONCATENATES these onto `className` rather than
      // replacing it, so repeating the base would emit it twice into the DOM.
      activeProps={{ className: NAV_PILL_ACTIVE }}
      inactiveProps={{ className: NAV_PILL_IDLE }}
    >
      <span className={NAV_ICON_LIFT}>
        <NavIcon name={animation} fallback={icon} playing={hot} className={NAV_ICON} />
      </span>
      <span className={NAV_LABEL}>{label}</span>
    </Link>
  );
}
